import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabaseService } from '../services/supabaseService';
import { db } from '../config/firebase';
import { AppUser } from '../types';

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<Partial<AppUser>>({});
  const [saving, setSaving] = useState(false);
  
  // Abone yönetimi için state'ler
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  
  // Moderasyon işlemleri için state'ler
  const [moderationLoading, setModerationLoading] = useState(false);
  const [userReports, setUserReports] = useState<any[]>([]);
  const [showReportsModal, setShowReportsModal] = useState(false);

  // Kullanıcı verilerini yükle (Supabase ile)
  useEffect(() => {
    const loadUser = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 SUPABASE: Kullanıcı detayı yükleniyor, ID:', userId);
        
        // Supabase'den kullanıcı getir
        const userData = await supabaseService.getUserById(userId);
        
        if (userData) {
          console.log('✅ SUPABASE: Kullanıcı bulundu:', userData);
          
          // Premium abonelik bilgilerini getir (Supabase'den)
          const premiumInfo = await getUserPremiumInfo(userId);
          
          const userObj: AppUser = {
            id: userData.id,
            email: userData.email || '',
            name: userData.name || '',
            age: userData.age || 18,
            gender: userData.gender || '',
            isActive: userData.isActive !== false,
            createdAt: userData.createdAt,
            lastActiveAt: userData.lastActiveAt,
            photos: userData.photos || [],
            bio: userData.bio || '',
            interests: userData.interests || [],
            relationshipStatus: userData.relationshipStatus || '',
            languages: userData.languages || [],
            // Premium ve abone bilgileri
            premiumInfo,
            subscriberCount: 0 // Placeholder
          };
          
          setUser(userObj);
          setEditedUser(userObj);
        } else {
          console.log('❌ SUPABASE: Kullanıcı bulunamadı, ID:', userId);
          setError('Kullanıcı bulunamadı');
        }
      } catch (err) {
        console.error('❌ SUPABASE: Kullanıcı yükleme hatası:', err);
        setError('Kullanıcı yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [userId]);

  // Premium abonelik bilgilerini getir (Supabase'den)
  const getUserPremiumInfo = async (userId: string) => {
    try {
      console.log('🔍 SUPABASE: Premium bilgi alınıyor, userId:', userId);
      
      // Supabase'de premium kontrol et
      const isPremium = await supabaseService.isPremiumUser(userId);
      
      if (isPremium) {
        const premiumInfo = await supabaseService.getUserPremiumInfo(userId);
        console.log('✅ SUPABASE: Premium bilgi bulundu:', premiumInfo);
        return premiumInfo;
      }
      
      console.log('❌ SUPABASE: Bu kullanıcı premium değil');
      return {
        isPremium: false,
        packageType: null,
        price: 0,
        startDate: null,
        endDate: null,
        isActive: false
      };
    } catch (error) {
      console.error('💥 SUPABASE: Premium bilgi getirme hatası:', error);
      return {
        isPremium: false,
        packageType: null,
        price: 0,
        startDate: null,
        endDate: null,
        isActive: false
      };
    }
  };

  // Kullanıcı durumunu değiştir (aktif/pasif) - Supabase ile
  const handleStatusToggle = async () => {
    if (!user) return;
    
    try {
      const newStatus = !user.isActive;
      console.log('🔄 SUPABASE: Kullanıcı durumu güncelleniyor:', newStatus);
      
      await supabaseService.updateUserStatus(user.id, newStatus);
      
      setUser({ ...user, isActive: newStatus });
      alert(`Kullanıcı ${newStatus ? 'aktif' : 'pasif'} duruma getirildi!`);
    } catch (error) {
      console.error('❌ SUPABASE: Durum güncelleme hatası:', error);
      alert('Durum güncellenirken hata oluştu!');
    }
  };

  // Kullanıcı bilgilerini kaydet - Supabase ile
  const handleSave = async () => {
    if (!user || !editedUser) return;
    
    try {
      setSaving(true);
      
      console.log('💾 SUPABASE: Kullanıcı bilgileri güncelleniyor...');
      
      await supabaseService.updateUserProfile(user.id, editedUser);
      
      setUser({ ...user, ...editedUser });
      setIsEditing(false);
      alert('Kullanıcı bilgileri başarıyla güncellendi!');
    } catch (error) {
      console.error('❌ SUPABASE: Kaydetme hatası:', error);
      alert('Bilgiler kaydedilirken hata oluştu!');
    } finally {
      setSaving(false);
    }
  };

  // Tarih formatla
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Bilinmiyor';
    
    try {
      let date;
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    } catch (error) {
      return 'Bilinmiyor';
    }
  };

  // Kullanıcının detaylı abone listesini yükle
  const loadSubscribers = async () => {
    if (!user) return;
    
    try {
      setLoadingSubscribers(true);
      // Bu özellik şu anda Supabase'de mevcut değil
      alert('Bu özellik yakında eklenecek (Supabase geçişi)');
      setSubscribers([]);
    } catch (error) {
      console.error('Abone listesi yükleme hatası:', error);
      alert('Abone listesi yüklenirken hata oluştu!');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  // Manuel abone ekleme
  const handleAddManualSubscriber = async () => {
    if (!user) return;
    
    alert('Bu özellik yakında eklenecek (Supabase geçişi sırasında)');
    return;
    
    // Bu fonksiyon şu anda Supabase'de mevcut değil
    // Gelecekte eklenecek
  };

  // Manuel abone silme
  const handleRemoveSubscriber = async (subscriptionId: string, isManual: boolean) => {
    alert('Bu özellik yakında eklenecek (Supabase geçişi sırasında)');
    return;
    
    // Bu fonksiyon şu anda Supabase'de mevcut değil
  };

  // Kullanıcıya manuel jeton ekleme
  const handleAddTokens = async () => {
    if (!user) return;
    
    alert('Bu özellik yakında eklenecek (Supabase geçişi sırasında)');
    return;
    
    // Bu fonksiyon şu anda Supabase'de mevcut değil
  };

  // Kullanıcı silme fonksiyonu
  const handleDeleteUser = async () => {
    if (!user) return;
    
    console.log('🔍 SUPABASE: Silme işlemi başlıyor, kullanıcı:', user.name, user.id);
    
    const reason = prompt(`"${user.name}" adlı kullanıcıyı silmek istediğinizden emin misiniz?\n\nSilme sebebi:`);
    if (reason === null) {
      console.log('Kullanıcı silme iptal edildi');
      return;
    }
    
    const confirmText = `KULLANICI SİL`;
    const userConfirm = prompt(`Bu işlem geri alınamaz! Onaylamak için "${confirmText}" yazın:`);
    
    if (userConfirm !== confirmText) {
      alert('Onaylama metni hatalı. İşlem iptal edildi.');
      return;
    }
    
    try {
      console.log('🚀 SUPABASE: Silme işlemi başlıyor...');
      setLoading(true);
      
      await supabaseService.deleteUser(user.id);
      console.log('✅ SUPABASE: Silme işlemi tamamlandı');
      
      alert(`"${user.name}" adlı kullanıcı başarıyla silindi.`);
      
      // Ana sayfaya yönlendir
      console.log('🔄 SUPABASE: Ana sayfaya yönlendiriliyor...');
      navigate('/users');
    } catch (error) {
      console.error('💥 SUPABASE: Kullanıcı silme hatası:', error);
      
      // Hata detaylarını göster
      let errorMessage = 'Kullanıcı silinirken hata oluştu!';
      if (error instanceof Error) {
        errorMessage += `\n\nHata detayı: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcıyı engelleme fonksiyonu
  const handleBlockUser = async (targetUserId: string) => {
    if (!window.confirm(`"${user?.name}" adlı kullanıcıyı engellemek istediğinizden emin misiniz?\n\nBu işlem kullanıcının uygulamayı kullanmasını kısıtlar.`)) {
      return;
    }

    try {
      setModerationLoading(true);
      
      // Supabase'de kullanıcı durumunu pasif yap
      await supabaseService.updateUserStatus(targetUserId, false);
      
      // Yerel state'i güncelle
      if (user && user.id === targetUserId) {
        setUser({ ...user, isActive: false });
      }
      
      alert('Kullanıcı başarıyla engellendi!');
    } catch (error) {
      console.error('Engelleme hatası:', error);
      alert('Kullanıcı engellenirken hata oluştu!');
    } finally {
      setModerationLoading(false);
    }
  };

  // Kullanıcıyı raporlama fonksiyonu
  const handleReportUser = async (targetUserId: string) => {
    const reportReason = prompt(`"${user?.name}" adlı kullanıcıyı hangi sebeple raporlamak istiyorsunuz?\n\nMevcut sebepler:\n- Uygunsuz fotoğraflar\n- Spam mesajlar\n- Sahte profil\n- Taciz\n- Diğer\n\nRapor sebebi:`);
    
    if (!reportReason) {
      console.log('Rapor iptali');
      return;
    }

    const reportDescription = prompt('Rapor açıklaması (opsiyonel):');

    try {
      setModerationLoading(true);
      
      // Firebase'e rapor kaydet
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      
      const reportData = {
        reporterId: 'admin', // Admin tarafından yapılan rapor
        reportedUserId: targetUserId,
        reason: reportReason,
        description: reportDescription || 'Admin tarafından manuel rapor',
        status: 'pending',
        createdAt: serverTimestamp(),
        reportedBy: 'admin_panel'
      };

      await addDoc(collection(db, 'user_reports'), reportData);
      
      alert(`"${user?.name}" adlı kullanıcı başarıyla raporlandı!\n\nRapor sebebi: ${reportReason}\nRapor moderasyon ekibine iletildi.`);
    } catch (error) {
      console.error('Kullanıcı raporlama hatası:', error);
      alert('Kullanıcı raporlanırken hata oluştu!');
    } finally {
      setModerationLoading(false);
    }
  };

  // Kullanıcıya ait raporları görüntüleme
  const handleViewUserReports = async (targetUserId: string) => {
    try {
      setModerationLoading(true);
      
      // Firebase'den bu kullanıcıya ait raporları al
      const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
      
      const reportsQuery = query(
        collection(db, 'user_reports'),
        where('reportedUserId', '==', targetUserId),
        orderBy('createdAt', 'desc')
      );
      
      const reportsSnapshot = await getDocs(reportsQuery);
      const reports: any[] = [];
      
      reportsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        reports.push({
          id: doc.id,
          ...data
        });
      });
      
      setUserReports(reports);
      setShowReportsModal(true);
      
      if (reports.length === 0) {
        alert(`"${user?.name}" adlı kullanıcıya ait herhangi bir rapor bulunamadı.`);
      }
    } catch (error) {
      console.error('Kullanıcı raporları getirme hatası:', error);
      alert('Kullanıcı raporları alınırken hata oluştu!');
    } finally {
      setModerationLoading(false);
    }
  };

  // Kullanıcıyı premium yapma fonksiyonu
  const handleMakeUserPremium = async () => {
    if (!user) return;
    
    if (user.premiumInfo?.isPremium) {
      alert('Bu kullanıcı zaten premium aboneliğe sahip!');
      return;
    }
    
    try {
      setSaving(true);
      
      // Supabase'de premium abonelik oluştur
      const success = await supabaseService.createPremiumSubscription({
        userId: user.id,
        packageType: '1_month',
        price: 89.99,
        durationDays: 30,
        paymentMethod: 'admin_grant',
        adminNote: 'Admin tarafından manuel olarak verildi'
      });
      
      if (success) {
        // Premium bilgilerini yenile
        const updatedPremiumInfo = await getUserPremiumInfo(user.id);
        setUser({ ...user, premiumInfo: updatedPremiumInfo });
        
        alert(`✨ Başarılı! "${user.name}" adlı kullanıcıya 1 aylık premium abonelik verildi.`);
      } else {
        alert('Premium abonelik oluşturulurken hata oluştu!');
      }
    } catch (error) {
      console.error('Premium abonelik hatası:', error);
      alert('Premium abonelik oluşturulurken hata oluştu!');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Kişisel Bilgiler', icon: '👤' },
    { id: 'interests', label: 'İlgi Alanları', icon: '❤️' },
    { id: 'premium', label: 'Premium & Abonelik', icon: '💎' },
    { id: 'contact', label: 'Konum & İletişim', icon: '📞' },
    { id: 'matches', label: 'Eşleşmeler', icon: '💕' }
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #F78D93',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p style={{ color: '#666' }}>Kullanıcı bilgileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        backgroundColor: '#f8d7da',
        borderRadius: '8px',
        color: '#721c24'
      }}>
        <h3>⚠️ Hata</h3>
        <p>{error}</p>
        <button 
          onClick={() => navigate('/users')}
          style={{
            backgroundColor: '#F78D93',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Kullanıcı Listesine Dön
        </button>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => navigate('/users')}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}
        >
          ← Geri Dön
        </button>
      </div>

      {/* User Profile Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }}>
          {/* Profile Picture */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {user.photos && user.photos.length > 0 ? (
              <img
                src={user.photos[0]}
                alt={user.name || 'Kullanıcı'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  border: '4px solid #F78D93'
                }}
                onError={(e) => {
                  // Fotoğraf yüklenemezse default avatar göster
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.backgroundColor = '#F78D93';
                    parent.style.display = 'flex';
                    parent.style.alignItems = 'center';
                    parent.style.justifyContent = 'center';
                    parent.style.fontSize = '3rem';
                    parent.style.fontWeight = 'bold';
                    parent.style.color = 'white';
                    parent.innerHTML = user.name ? user.name.charAt(0).toUpperCase() : 'U';
                  }
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#F78D93',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                fontWeight: 'bold',
                color: 'white'
              }}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </div>

          {/* User Info */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <h1 style={{ 
                color: '#2c3e50', 
                margin: 0,
                fontSize: '2rem',
                fontWeight: '600'
              }}>
                {user.name || 'İsimsiz Kullanıcı'}
              </h1>
              <span style={{ 
                fontSize: '1.2rem', 
                color: '#666',
                backgroundColor: '#f8f9fa',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px'
              }}>
                ({user.age || 'Bilinmiyor'} yaş)
              </span>
              <span style={{
                backgroundColor: user.isActive ? '#28a745' : '#dc3545',
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                {user.isActive ? '✅ Doğrulanmış' : '❌ Pasif'}
              </span>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F78D93' }}>
                  {user.photos?.length || 0}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Fotoğraf</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F78D93' }}>
                  {user.interests?.length || 0}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>İlgi Alanı</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                  {user.subscriberCount || 0}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Abone</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: 'bold', 
                  color: user.premiumInfo?.isPremium ? '#f39c12' : '#999' 
                }}>
                  {user.premiumInfo?.isPremium ? '💎' : '🆓'}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                  {user.premiumInfo?.isPremium ? 'Premium' : 'Ücretsiz'}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setIsEditing(!isEditing)}
                style={{
                  backgroundColor: isEditing ? '#6c757d' : '#F78D93',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isEditing ? '❌ İptal Et' : '✏️ Düzenle'}
              </button>

              {isEditing && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  {saving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
                </button>
              )}

              <button
                onClick={handleDeleteUser}
                disabled={loading}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? '⏳ Siliniyor...' : '🗑️ Kullanıcıyı Sil'}
              </button>
            </div>

            {/* User Moderation Actions */}
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              backgroundColor: '#fff3cd',
              borderRadius: '8px',
              border: '2px solid #ffc107'
            }}>
              <h4 style={{ 
                margin: '0 0 1rem 0',
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🛡️ Kullanıcı Moderasyon İşlemleri
              </h4>
              
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleStatusToggle}
                  style={{
                    backgroundColor: user.isActive ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {user.isActive ? '⏸️ Kullanıcıyı Pasifleştir' : '▶️ Kullanıcıyı Aktifleştir'}
                </button>
                
                <button
                  onClick={() => handleBlockUser(user.id)}
                  disabled={moderationLoading}
                  style={{
                    backgroundColor: '#fd7e14',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: moderationLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: moderationLoading ? 0.6 : 1
                  }}
                >
                  {moderationLoading ? '⏳' : '🚫'} Kullanıcıyı Engelle
                </button>
                
                <button
                  onClick={() => handleReportUser(user.id)}
                  disabled={moderationLoading}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: moderationLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: moderationLoading ? 0.6 : 1
                  }}
                >
                  {moderationLoading ? '⏳' : '⚠️'} Kullanıcıyı Raporla
                </button>
                
                <button
                  onClick={() => handleViewUserReports(user.id)}
                  disabled={moderationLoading}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    cursor: moderationLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: moderationLoading ? 0.6 : 1
                  }}
                >
                  {moderationLoading ? '⏳' : '📋'} Bu Kullanıcıya Ait Raporlar
                </button>
              </div>
              
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fff',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: '#6c757d'
              }}>
                <strong>⚠️ Dikkat:</strong> Bu işlemler kullanıcının deneyimini doğrudan etkiler. 
                Engelleme işlemi kullanıcının uygulamayı kullanmasını kısıtlar, 
                raporlama işlemi ise moderasyon ekibine bilgi gönderir.
              </div>
            </div>
          </div>
        </div>

        {/* Meta Info */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <div>
            <strong>Kayıt tarihi:</strong> {formatDate(user.createdAt)}
          </div>
          <div>
            <strong>Son giriş:</strong> {formatDate(user.lastActiveAt)}
          </div>
          <div>
            <strong>Kullanıcı ID:</strong> {user.id}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Tab Headers */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '1rem',
                border: 'none',
                backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#F78D93' : '#666',
                borderBottom: activeTab === tab.id ? '3px solid #F78D93' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: activeTab === tab.id ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: '2rem' }}>
          {activeTab === 'personal' && (
            <div>
              <h3 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '1.5rem' }}>
                Kişisel Bilgiler
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                <div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      📧 E-posta
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editedUser.email || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e6ed',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        color: '#495057'
                      }}>
                        {user.email || 'E-posta belirtilmemiş'}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      👤 Ad Soyad
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedUser.name || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e6ed',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        color: '#495057'
                      }}>
                        {user.name || 'İsim belirtilmemiş'}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      🎂 Yaş
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        min="18"
                        max="99"
                        value={editedUser.age || 18}
                        onChange={(e) => setEditedUser({ ...editedUser, age: parseInt(e.target.value) })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e6ed',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        color: '#495057'
                      }}>
                        {user.age || 'Yaş belirtilmemiş'}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      ⚧ Cinsiyet
                    </label>
                    {isEditing ? (
                      <select
                        value={editedUser.gender || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, gender: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e6ed',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="">Seçiniz</option>
                        <option value="Erkek">Erkek</option>
                        <option value="Kadın">Kadın</option>
                        <option value="Diğer">Diğer</option>
                      </select>
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        color: '#495057'
                      }}>
                        {user.gender || 'Cinsiyet belirtilmemiş'}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      💕 İlişki Durumu
                    </label>
                    {isEditing ? (
                      <select
                        value={editedUser.relationshipStatus || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, relationshipStatus: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e6ed',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="">Seçiniz</option>
                        <option value="single">Bekar</option>
                        <option value="relationship">İlişkide</option>
                        <option value="married">Evli</option>
                        <option value="divorced">Boşanmış</option>
                        <option value="widowed">Dul</option>
                      </select>
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        color: '#495057'
                      }}>
                        {user.relationshipStatus || 'Belirtilmemiş'}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      📝 Biyografi
                    </label>
                    {isEditing ? (
                      <textarea
                        value={editedUser.bio || ''}
                        onChange={(e) => setEditedUser({ ...editedUser, bio: e.target.value })}
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #e0e6ed',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          boxSizing: 'border-box',
                          resize: 'vertical'
                        }}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        color: '#495057',
                        minHeight: '100px'
                      }}>
                        {user.bio || 'Biyografi eklenmemiş'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'interests' && (
            <div>
              <h3 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '1.5rem' }}>
                İlgi Alanları
              </h3>
              
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                {user.interests && user.interests.length > 0 ? (
                  user.interests.map((interest, index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: '#F78D93',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      {interest}
                    </span>
                  ))
                ) : (
                  <p style={{ color: '#666', fontStyle: 'italic' }}>
                    Henüz ilgi alanı eklenmemiş.
                  </p>
                )}
              </div>

              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '1rem' }}>Konuşulan Diller</h4>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem'
                }}>
                  {user.languages && user.languages.length > 0 ? (
                    user.languages.map((language, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        {language}
                      </span>
                    ))
                  ) : (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                      Dil bilgisi eklenmemiş.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'premium' && (
            <div>
              <h3 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '1.5rem' }}>
                Premium Abonelik & Kullanıcı Abonelikleri
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                {/* Premium Abonelik Bilgileri */}
                <div style={{
                  backgroundColor: user.premiumInfo?.isPremium ? '#fff8e1' : '#f8f9fa',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: user.premiumInfo?.isPremium ? '2px solid #f39c12' : '2px solid #e9ecef'
                }}>
                  <h4 style={{ 
                    margin: '0 0 1rem 0', 
                    color: '#2c3e50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    💎 Premium Abonelik Durumu
                  </h4>
                  
                  {user.premiumInfo?.isPremium ? (
                    <div>
                      <div style={{
                        backgroundColor: '#f39c12',
                        color: 'white',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        textAlign: 'center',
                        fontWeight: '600'
                      }}>
                        ✨ PREMİUM KULLANICI ✨
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Paket Türü:</strong>
                        <div style={{
                          backgroundColor: '#f39c12',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          display: 'inline-block',
                          marginLeft: '0.5rem'
                        }}>
                          {user.premiumInfo.packageType === '1_month' ? '1 Aylık Premium' :
                           user.premiumInfo.packageType === '3_month' ? '3 Aylık Premium' :
                           user.premiumInfo.packageType === '6_month' ? '6 Aylık Premium' :
                           user.premiumInfo.packageType === '12_month' ? '12 Aylık Premium' :
                           'Bilinmeyen Paket'}
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Ödenen Tutar:</strong>
                        <span style={{ marginLeft: '0.5rem', color: '#f39c12', fontWeight: '600' }}>
                          {user.premiumInfo.price || 0}₺
                        </span>
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Başlangıç Tarihi:</strong>
                        <span style={{ marginLeft: '0.5rem' }}>
                          {formatDate(user.premiumInfo.startDate)}
                        </span>
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <strong>Bitiş Tarihi:</strong>
                        <span style={{ marginLeft: '0.5rem' }}>
                          {formatDate(user.premiumInfo.endDate)}
                        </span>
                      </div>
                      
                      <div style={{
                        backgroundColor: '#d4edda',
                        color: '#155724',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.9rem'
                      }}>
                        🌟 Bu kullanıcı premium özelliklere erişebilir
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        textAlign: 'center',
                        fontWeight: '600'
                      }}>
                        🆓 ÜCRETSIZ KULLANICI
                      </div>
                      
                      <div style={{
                        backgroundColor: '#e2e3e5',
                        color: '#6c757d',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        marginBottom: '1rem'
                      }}>
                        📝 Bu kullanıcının henüz premium aboneliği bulunmuyor
                      </div>
                      
                      {/* Premium Yapma Butonu */}
                      <button
                        onClick={handleMakeUserPremium}
                        disabled={saving}
                        style={{
                          backgroundColor: '#f39c12',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          fontSize: '1rem',
                          fontWeight: '600',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          opacity: saving ? 0.6 : 1,
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          if (!saving) {
                            e.currentTarget.style.backgroundColor = '#e69500';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!saving) {
                            e.currentTarget.style.backgroundColor = '#f39c12';
                          }
                        }}
                      >
                        {saving ? '⏳ Premium Veriliyor...' : '✨ PREMIUM YAP (1 Ay)'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Kullanıcı Abonelik Yönetimi */}
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef'
                }}>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ 
                      margin: 0, 
                      color: '#2c3e50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      👥 Abone Yönetimi
                    </h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={loadSubscribers}
                        disabled={loadingSubscribers}
                        style={{
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: loadingSubscribers ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          opacity: loadingSubscribers ? 0.6 : 1
                        }}
                      >
                        {loadingSubscribers ? '🔄 Yükleniyor...' : '📊 Abone Listesi'}
                      </button>
                      <button
                        onClick={handleAddManualSubscriber}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        ➕ Manuel Abone Ekle
                      </button>
                      <button
                        onClick={handleAddTokens}
                        style={{
                          backgroundColor: '#ffc107',
                          color: 'black',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        🪙 Jeton Ekle
                      </button>
                    </div>
                  </div>
                  
                  {/* Abone Sayı Özeti */}
                  <div style={{
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    textAlign: 'center',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      {user.subscriberCount || 0}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                      Toplam Aktif Abone
                    </div>
                  </div>
                  
                  {/* Abone Listesi */}
                  {subscribers.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <h5 style={{ color: '#2c3e50', marginBottom: '1rem' }}>📝 Abone Detayları:</h5>
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {subscribers.map((subscriber, index) => (
                          <div
                            key={subscriber.id}
                            style={{
                              padding: '1rem',
                              borderBottom: index < subscribers.length - 1 ? '1px solid #f0f0f0' : 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                <span style={{
                                  backgroundColor: subscriber.addedByAdmin ? '#28a745' : '#17a2b8',
                                  color: 'white',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}>
                                  {subscriber.addedByAdmin ? '🔧 Admin' : '👤 Kullanıcı'}
                                </span>
                                <span style={{ fontWeight: '500' }}>
                                  {subscriber.subscriberDetails?.name || subscriber.subscriberId}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                {subscriber.subscriberDetails?.email || 'E-posta yok'}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#999' }}>
                                Eklenme: {formatDate(subscriber.createdAt)}
                              </div>
                              {subscriber.adminNote && (
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  color: '#6c757d',
                                  fontStyle: 'italic',
                                  marginTop: '0.25rem'
                                }}>
                                  📝 {subscriber.adminNote}
                                </div>
                              )}
                            </div>
                            
                            {subscriber.addedByAdmin && (
                              <button
                                onClick={() => handleRemoveSubscriber(subscriber.id, true)}
                                style={{
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 0.75rem',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem'
                                }}
                                title="Manuel aboneyi sil"
                              >
                                🗑️ Sil
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div style={{
                    backgroundColor: '#d1ecf1',
                    color: '#0c5460',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    marginTop: '1rem'
                  }}>
                    💡 <strong>Admin Yetkisi:</strong> Bu sayfadan manuel abone ekleyebilir ve silebilirsiniz. 
                    Eklenen aboneler gerçek kullanıcılar gibi sayılır ve uygulamada görünür.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div>
              <h3 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '1.5rem' }}>
                İletişim Bilgileri
              </h3>
              
              <div style={{
                padding: '2rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p>📍 Konum ve iletişim bilgileri</p>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Bu bölüm geliştirilme aşamasında...
                </p>
              </div>
            </div>
          )}

          {activeTab === 'matches' && (
            <div>
              <h3 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '1.5rem' }}>
                Eşleşmeler
              </h3>
              
              <div style={{
                padding: '2rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p>💕 Kullanıcının eşleşmeleri</p>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Bu bölüm geliştirilme aşamasında...
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Kullanıcı Raporları Modal */}
      {showReportsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>
                📋 {user?.name} - Kullanıcı Raporları
              </h3>
              <button
                onClick={() => setShowReportsModal(false)}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                ✕
              </button>
            </div>

            {userReports.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
                <h4>Rapor bulunamadı</h4>
                <p>Bu kullanıcıya ait herhangi bir rapor bulunmuyor.</p>
              </div>
            ) : (
              <div>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Toplam {userReports.length} rapor bulundu
                </p>
                
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  {userReports.map((report, index) => (
                    <div
                      key={report.id}
                      style={{
                        padding: '1.5rem',
                        backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid #e9ecef'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <h5 style={{ 
                            margin: '0 0 0.5rem 0', 
                            color: '#dc3545',
                            fontSize: '1.1rem'
                          }}>
                            ⚠️ {report.reason || 'Sebep belirtilmemiş'}
                          </h5>
                          <div style={{ fontSize: '0.9rem', color: '#666' }}>
                            Rapor ID: {report.id}
                          </div>
                        </div>
                        
                        <span style={{
                          backgroundColor: 
                            report.status === 'pending' ? '#ffc107' :
                            report.status === 'reviewing' ? '#17a2b8' :
                            report.status === 'resolved' ? '#28a745' : '#dc3545',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {report.status === 'pending' ? 'Beklemede' :
                           report.status === 'reviewing' ? 'İnceleniyor' :
                           report.status === 'resolved' ? 'Çözüldü' : 'Reddedildi'}
                        </span>
                      </div>
                      
                      {report.description && (
                        <div style={{
                          backgroundColor: '#fff',
                          padding: '1rem',
                          borderRadius: '6px',
                          marginBottom: '1rem',
                          fontSize: '0.9rem',
                          color: '#495057'
                        }}>
                          <strong>Açıklama:</strong> {report.description}
                        </div>
                      )}
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                        color: '#6c757d'
                      }}>
                        <div>
                          <strong>Raporlayan:</strong> {report.reporterId || 'Bilinmiyor'}
                        </div>
                        <div>
                          <strong>Tarih:</strong> {formatDate(report.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailPage; 